/**
 * Modelo de los tours guiados.
 *
 * Un tour es una lista de pasos; cada paso señala un elemento real de la pantalla por
 * su atributo `data-tour`, no por clase ni por id, para que un refactor de estilos no
 * rompa la guía. El motor es Shepherd (`GuidedTourService`).
 *
 * La idea que ordena todo el modelo: **el tour sigue a la persona, no al revés**. Los
 * pasos que hablan de algo que todavía no está en pantalla —un diálogo, el detalle de
 * un documento— no se muestran «en el vacío»: antes va un paso *puerta* (`waitFor`)
 * que señala el botón que hay que tocar y espera; en cuanto el elemento aparece, el
 * tour avanza solo. Y los pasos que viven dentro de ese diálogo se marcan con
 * `requires`, para que si se cierra el tour regrese a la puerta en vez de seguir
 * explicando campos que ya nadie ve.
 */

/** Lado por el que sale el globo respecto al elemento señalado. */
export type TourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

export interface TourStep {
    /**
     * Valor del atributo `data-tour` del elemento a señalar. Si se omite, el paso se
     * muestra centrado en la pantalla (útil para la introducción y el cierre).
     */
    anchor?: string;

    title: string;

    /** Cuerpo del paso. Admite marcado simple (`<b>`, `<code>`, `<ul>`). */
    body: string;

    /** Nota al pie, para decir qué tiene que hacer la persona antes de seguir. */
    hint?: string;

    placement?: TourPlacement;

    /**
     * Ruta en la que tiene que estar la pantalla para mostrar el paso. Si no es la
     * actual, el tour navega solo. Se compara exacta: un paso del listado devuelve a
     * la persona al listado aunque esté en el detalle.
     */
    route?: string;

    /**
     * Convierte el paso en una **puerta**: el tour se queda aquí hasta que aparezca
     * en pantalla el `data-tour` indicado, y entonces avanza solo.
     *
     * Es lo que hace que «da clic en Nuevo documento» continúe *dentro* del diálogo,
     * y que «abre el documento» continúe *dentro* del detalle.
     */
    waitFor?: string;

    /**
     * Lo contrario: la puerta se abre cuando el `data-tour` indicado **desaparece**.
     * Sirve para esperar a que se cierre un diálogo antes de seguir con la pantalla
     * de atrás.
     */
    waitForGone?: string;

    /**
     * `data-tour` que tiene que seguir en pantalla mientras el paso se muestra. Si
     * desaparece —se cerró el diálogo, se salió del detalle— el tour vuelve a la
     * última puerta en lugar de explicar algo invisible.
     */
    requires?: string;

    /**
     * `data-tour` del mensaje de «no hay nada» de la pantalla. Si está visible, el
     * paso no puede pedir lo que pide —no hay documento que abrir, no hay área que
     * editar—: se muestra `emptyBody` en lugar del cuerpo normal y se deja de
     * prometer que el tour avanzará solo.
     */
    emptyAnchor?: string;

    /** Qué decir cuando la pantalla está vacía. Sin esto, `emptyAnchor` no hace nada. */
    emptyBody?: string;

    /** Si devuelve `false` el paso se omite (permisos, estado de la pantalla). */
    when?: () => boolean;

    /**
     * Cuánto esperar a que aparezca el ancla antes de mostrar el paso, en ms. Con las
     * puertas casi nunca hace falta: el elemento ya está ahí. Por omisión es corto
     * justo para que no se sienta un frenón.
     */
    waitMs?: number;
}

export interface Tour {
    /** Identificador estable: se usa para recordar que ya se vio. */
    id: string;

    title: string;

    description?: string;

    /** Icono PrimeIcons, para la lista de tours del panel de ayuda. */
    icon: string;

    /** Si devuelve `false` el tour no se ofrece (típicamente por permiso). */
    when?: () => boolean;

    steps: TourStep[];
}

/** Sección estática del panel de ayuda: referencia, no walkthrough. */
export interface HelpSection {
    id: string;
    title: string;
    icon: string;
    /** Marcado simple; se pinta con `innerHTML` (Angular lo sanitiza). */
    html: string;
}
